import axios from 'axios';
import React, { useRef, useEffect, useState, useContext } from 'react';
import ARCMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Basemap from '@arcgis/core/Basemap.js';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import Extent from '@arcgis/core/geometry/Extent.js';
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js';
import { InterfaceContext } from './store/InterfaceProvider';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';
import { generateChloroplethStyleJson, generateInfoStyleJson, generateHoverStyleJson } from './utils/generateVectorTileStyleJson';
import { DATA_URL, getScaleObject } from './store/StaticVariables';
import { geometryScaleFromGeoid } from './utils/DataTransformation';
import esriConfig from '@arcgis/core/config.js';

const halfmapurl = 'https://witv-grsp-5.cdc.gov/onemapservices/rest/services/Hosted/Dashboard_2020/VectorTileServer/resources/styles/root.json';
const devmapurl = 'https://witv-grsp-5.cdc.gov/onemapservices/rest/services/Hosted/Dev_Dashboard_2020/VectorTileServer/resources/styles/root.json';

const serviceToken = 'GQh67OHQNscFyEASmoJn02PiXe10101Sm-5YAGDEO7Ij1-OuRukgMjLmGVTvjKQOy7s68fSDWRhcINYnjzc1i8FMklRVfo257nWUiZ5V9neTpx4lFLL48_7BxqwfZm_LuAh6uMjKQPmKmRNLQzBAq73W65GXQVYwywWw5fsy3wofGAcqzABx4SCKhTESOtXz-kJ3vuPT_88UcuClqF-0nA..';
const serviceToken2 = 'P-FZSvYcjQfk9QfXPtJ4GIm_JwNw9KIHE-Iu4Q3lySZouEaRuRV9h_NJ6ma0CMBLHqK1ZzPuOP495m7vCwNeUIZGQbka4BFX0eqAzthjjDgUu-0UFDqVl9QHqqG1nZmehYNMNKAzo9UXlpa1rP9qG7-D5FaLeppRSy1Qnp0bpk8AuQp8x4jJXKtZo0itfFnt';

const MapSimple = () => {
    const { activeViewType, activeScale, activeTimePeriod, activeDeathType, activeMeasurement, mapReadOnlyZoom, selectedFeature, setSelectedFeature, setMapReadOnlyZoom, setHoveredFeature, activeMapAttribute } = useContext(InterfaceContext);

    const mapDiv = useRef(null);
    const textTokenRef = useRef(null);
    const textRef = useRef(null);
    const [selectedData, setSelectedData] = useState('');
    const [initMap, setInitMap] = useState(false);
    const view = useRef(null);
    const map = useRef(null);
    const chloroplethLayer = useRef(null);
    const chloroplethLoading = useRef(false);
    const infoLayer = useRef(null);
    const zoomRef = useRef(mapReadOnlyZoom);
    const dataRef = useRef({
        scale: activeScale,
        view: activeViewType,
        timePeriod: activeTimePeriod,
        deathType: activeDeathType,
        measurment: activeMeasurement
    });

    const resultWithinHitLayer = (result) => {
        return result?.layer?.id === 'hit-layer';
    };
    const resultWithinScale = (result) => {
        var geoid = result.graphic.attributes?.GEOID || result.graphic.attributes?.geoid || false;
        const geoScale = geometryScaleFromGeoid(geoid);
        return geoid && geoScale === dataRef.current.scale;
    };
    const filterGraphicsByHitLayer = (results) => {
        return results.filter((result) => {
            return resultWithinHitLayer(result);
        });
    };
    const getAttributesFromInteraction = (response) => {
        if (!response?.results?.length) return {};
        const validResults = filterGraphicsByHitLayer(response.results);
        if (!validResults.length) return {};
        return validResults.map((r) => r.graphic.attributes);
    };

    const handleFeatureClick = (event) => {
        view.current.hitTest(event).then((res) => {
            const feature = getAttributesFromInteraction(res);
            console.log(feature);
            setSelectedData(JSON.stringify(feature, null, 2));
        });
    };
    const addToken = () => {
        const token = textTokenRef.current.value;
        esriConfig.request.interceptors = [];
        if (!token) return;
        var indexOfVector = textRef.current.value.indexOf('VectorTileServer');
        var vecTileUrl = textRef.current.value.substring(0, indexOfVector);
        const interceptor = {
            urls: [vecTileUrl],
            before({ url, requestOptions }) {
                requestOptions.query.token = token;
                // requestOptions.headers = { 'X-Esri-Authorization': 'Bearer ' + token };
                // requestOptions.headers = { 'Authorization': 'Bearer ' + token };
            }
        };
        esriConfig.request.interceptors = [interceptor];
    };
    const loadAndParseStyle = async (url) => {
        const token = textTokenRef.current.value;
        const serviceUrlIndex = url.indexOf('VectorTileServer');
        var serviceUrl = url.substring(0, serviceUrlIndex) + 'VectorTileServer';
        console.log('Service url: ' + serviceUrl);
        return axios.get(url + '?token=' + token).then((res) => {
            return {
                version: 8,
                sprite: 'https://www.arcgis.com/sharing/rest/content/items/2b22eb2401ca455484d68e4c17ce251a/resources/sprites/sprite-1716407536684',
                glyphs: serviceUrl + '/resources/styles/fonts/{fontstack}/{range}.pbf',
                sources: {
                    esri: {
                        type: 'vector',
                        bounds: [-179.149, 18.9104, 179.778, 71.3878],
                        minzoom: 0,
                        maxzoom: 11,
                        scheme: 'xyz',
                        url: serviceUrl
                    }
                },
                layers: res.data.layers
            };
        });
    };
    const changeURLBU = (e) => {
        map.current.remove(chloroplethLayer.current);
        const token = textTokenRef.current.value;
        if (token) {
            loadAndParseStyle(textRef.current.value).then((style) => {
                addToken();
                chloroplethLayer.current = new VectorTileLayer({
                    id: 'hit-layer',
                    blendMode: 'multiply',
                    style
                });
                map.current.add(chloroplethLayer.current);
            });
        } else {
            chloroplethLayer.current = new VectorTileLayer({
                id: 'hit-layer',
                blendMode: 'multiply',
                url: textRef.current.value
            });
            map.current.add(chloroplethLayer.current);
        }
    };
    const changeURL = (e) => {
        console.log('val', textRef.current.value);
        addToken();
        map.current.remove(chloroplethLayer.current);
        chloroplethLayer.current = new VectorTileLayer({
            id: 'hit-layer',
            blendMode: 'multiply',
            url: textRef.current.value
        });
        map.current.add(chloroplethLayer.current);
    };

    useEffect(() => {
        if (!mapDiv.current && !initMap) return;
        setInitMap(true);
    }, [mapDiv.current]);

    useEffect(() => {
        if (!initMap) return;
        if (map.current !== null) return;

        // esriConfig.apiKey = serviceToken;
        const mapBaseLayer = new VectorTileLayer({
            url: DATA_URL + '/style/style-basemap-1.json'
        });

        // Create a Basemap with the VectorTileLayer
        const customBasemap = new Basemap({
            baseLayers: [mapBaseLayer],
            title: 'Terrain'
        });

        map.current = new ARCMap({ basemap: customBasemap });

        //US extent
        const extent = new Extent({
            xmin: -14150703.25072198,
            ymin: 1724460.655959669,
            xmax: -7075872.59319234,
            ymax: 6815350.164313462,
            spatialReference: new SpatialReference({ wkid: 3857 })
        });

        view.current = new MapView({
            container: mapDiv.current,
            map: map.current,
            extent: extent,
            constraints: {
                minZoom: 15,
                maxZoom: 3
            }
        });
        chloroplethLayer.current = new VectorTileLayer({
            id: 'hit-layer',
            blendMode: 'multiply',
            url: textRef.current.value
        });

        reactiveUtils.watch(
            () => [view.current.stationary, view.current.zoom, view.current.updating],
            ([stationary, zoom, updating]) => {
                if (stationary && !updating) {
                    if (zoomRef.current !== zoom) {
                        zoomRef.current = zoom;
                        setMapReadOnlyZoom(zoom);
                    }
                }
            }
        );

        view.current
            .when()
            .then(() => {
                return chloroplethLayer.current.when();
            })
            .then((layer) => {
                return view.current.whenLayerView(layer);
            })

            .then((layerView) => {
                view.current.on('immediate-click', handleFeatureClick);
            });
        map.current.add(chloroplethLayer.current);
    }, [initMap]);

    return (
        <>
            <div id="map-view-container" ref={mapDiv} style={{ height: '500px', windth: '500px', boxSizing: 'border-box' }}></div>
            <input ref={textTokenRef} style={{ width: '100%', padding: '5px' }} placeholder="Token" defaultValue={''}></input>
            <input ref={textRef} style={{ width: '100%', padding: '5px' }} placeholder="Path to root.json" defaultValue={halfmapurl}></input>
            <button onClick={changeURL}>Load style</button>
            <pre>{selectedData}</pre>
        </>
    );
};
export default MapSimple;
